import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isGermanLanguage } from '../../../utils/language';
import {
  AndMore,
  Box1mLine1,
  Box1mLine2,
  Box3mLine1,
  Box6mLine1,
  Box6mLine3,
  Free,
  Ingredients as IngredientsLabel,
  NutritionistAccess,
  PerDay,
  Reformulation,
  RiskFree,
  Shipping,
  TryOnceBox1,
  TryOnceBox2,
  TryOnceBox3,
  TryOnceBox12,
  WelcomeKit,
  WhatAreYouGetting,
  YouAre,
  Saving,
  InAverage,
  Available,
  OrderNow,
  AvailableText,
  SecurePayment,
  ShippedIn2To3Days,
  Verified,
  ProofTitle,
  ProofStat1,
  ProofStat2,
  ProofStat3,
  ProofStat4,
  ProofSource,
  CancelAnytimeLabel,
  CancelAnytimeAnswer,
} from '../../../Constants';
import GuaranteeImg from '../../../assets/Guarantee.webp';
import GiftImg from '../../../assets/Gift.webp';
import PaymentMethods from '../../../assets/payment-methods.webp';
import ReviewsImage from '../../../assets/reviews.webp';
import ShippingImg from '../../../assets/shipping-DHL.webp';
import HowToEnjoyAccordion from '../../Home/SmartFormulas/HowToEnjoyAccordion';
import { Icon } from '../../elements/icon';
import InfoCard from './InfoCard';

const ProofRow = ({ emoji, tile, stat, text, isLast }) => (
  <div className={`t-flex t-items-center t-gap-3 t-py-3 ${isLast ? '' : 't-border-b t-border-g200'}`}>
    <div
      className={`t-flex t-h-11 t-w-11 t-flex-shrink-0 t-items-center t-justify-center t-rounded-xl t-text-[20px] ${tile}`}
      aria-hidden="true"
    >
      {emoji}
    </div>
    <p className="!t-m-0 t-flex-1 t-font-sans t-text-[14px] t-leading-snug t-text-navy/80">
      <span className="t-mr-1.5 t-text-[19px] t-font-bold t-text-quiz-teal">{stat}</span>{' '}
      {text}
    </p>
  </div>
);

const ProofSection = ({ titleClassName = 't-text-center t-text-2xl' }) => {
  const { t, i18n } = useTranslation();
  const isGerman = isGermanLanguage(i18n.language);

  return (
    <div className="!t-mt-6 t-w-full t-rounded-2xl t-border t-border-g200 t-bg-white t-p-4 md:t-p-6">
      <p className={`!t-m-0 t-mb-2 t-font-serif t-text-navy ${titleClassName}`}>{t(ProofTitle)}</p>
      <div className="t-flex t-flex-col">
        <ProofRow emoji="📈" tile="t-bg-butter/20" stat="87%" text={t(ProofStat1)} />
        <ProofRow emoji="💬" tile="t-bg-quiz-teal/10" stat={isGerman ? '83 %' : '83%'} text={t(ProofStat2)} />
        <ProofRow emoji="🩺" tile="t-bg-navy/10" stat="40+" text={t(ProofStat3)} />
        <ProofRow emoji="👥" tile="t-bg-quaternary/20" stat={isGerman ? '5.000+' : '5000+'} text={t(ProofStat4)} isLast />
      </div>
      <p className="!t-m-0 t-mt-3 t-text-[11px] t-text-g500">{t(ProofSource)}</p>
    </div>
  );
};

const BenefitsList = ({
  duration,
  focusArea = [],
  totalIngredients,
  showOmega3Benefit,
  omega3BenefitKey,
}) => {
  const { t, i18n } = useTranslation();
  const isGerman = isGermanLanguage(i18n.language);
  const focusAreaNames = focusArea
    .slice(0, 2)
    .map((item) => item?.answer)
    .filter(Boolean);
  const items = [
    { durations: ['6 months'], text: t(Box6mLine1)?.replace('{0}', totalIngredients) },
    { durations: ['3 months'], text: t(Box3mLine1)?.replace('{0}', totalIngredients) },
    { durations: ['1 month'], text: t(Box1mLine1) },
    { durations: ['90 days'], text: t(TryOnceBox12)?.replace('{0}', totalIngredients) },
    { durations: ['30 days'], text: t(TryOnceBox1)?.replace('{0}', totalIngredients) },
  ];

  if (focusAreaNames.length > 0) {
    const focusAreaText = `${t(TryOnceBox3)} ${focusAreaNames.join(', ')} ${t(AndMore)}`;
    items.push({ durations: ['6 months', '3 months'], text: focusAreaText });
  }

  items.push(
    { durations: ['1 month'], text: t(Box1mLine2) },
    { durations: ['90 days', '30 days'], text: t(TryOnceBox2) },
    { durations: ['6 months', '3 months'], text: t(Box6mLine3) },
  );

  if (focusAreaNames.length > 0) {
    const focusAreaText = `${t(TryOnceBox3)} ${focusAreaNames.join(', ')} ${t(AndMore)}`;
    items.push({ durations: ['90 days', '30 days'], text: focusAreaText });
  }

  const freeItems = [
    { old: '6€', label: Shipping, copyKey: 'Product_FreeShippingValue' },
    { old: '20€', label: NutritionistAccess, copyKey: 'Product_FreeNutritionistValue' },
    { old: '10€', label: WelcomeKit, copyKey: 'Product_FreeWelcomeKitValue' },
  ];
  const isOneOff = ['90 days', '30 days'].includes(duration);

  return (
    <div className="t-flex t-flex-col !t-gap-2">
      {items
        .filter(({ durations }) => durations.includes(duration))
        .map((item, index) => (
          <div className="t-flex t-items-start t-gap-2" key={`${item.text}-${index}`}>
            <Icon name="tick" size="16" className="t-mt-[2px] t-flex-shrink-0" />
            <p className="!t-m-0 t-font-sans t-text-[13px] t-text-navy">{item.text}</p>
          </div>
        ))}

      {showOmega3Benefit && (
        <div className="t-flex t-items-start t-gap-2">
          <Icon name="tick" size="16" className="t-mt-[2px] t-flex-shrink-0" />
          <p className="!t-m-0 t-font-sans t-text-[13px] t-text-navy">
            {t(omega3BenefitKey || 'CustomFormula_Benefits_Omega3Included')}
          </p>
        </div>
      )}

      {!isOneOff &&
        freeItems.map(({ old, label, copyKey }) => (
          <div className="t-flex t-items-start t-gap-2" key={label}>
            <img src={GiftImg} alt="" className="t-h-4 t-w-4" />
            <p className="!t-m-0 t-font-sans t-text-[13px] t-text-navy">
              {isGerman ? (
                t(copyKey)
              ) : (
                <>
                  <span className="t-line-through t-opacity-50">{old}</span>{' '}
                  <span className="t-font-semibold t-text-quiz-teal">{t(Free)}</span> {t(label)}
                </>
              )}
            </p>
          </div>
        ))}

      <div className="t-flex t-items-start t-gap-2">
        <img src={GuaranteeImg} alt="" className="t-h-4 t-w-4" />
        <p className="!t-m-0 t-font-sans t-text-[13px] t-text-navy">
          <span className="t-font-semibold t-text-quiz-teal">{t(RiskFree)}</span> {t(Reformulation)}
        </p>
      </div>
    </div>
  );
};

const IngredientLabelList = ({ ingredients, showScientificName }) => {
  const { t } = useTranslation();
  const ingredientText = ingredients
    .map((item) => {
      if (!item?.ingredient) return null;
      if (!showScientificName || !item.scientific_name) return item.ingredient;
      return `${item.ingredient} (${item.scientific_name})`;
    })
    .filter(Boolean)
    .join(', ');
  const ingredientsDescription = t('IngredientsMicrogranuleDesc');
  const text = ingredientText ? `${ingredientText}, ${ingredientsDescription}` : ingredientsDescription;

  if (!text) return null;

  return (
    <div className="t-flex t-flex-col t-gap-2 t-rounded-lg t-border t-p-3">
      <p className="!t-m-0 t-font-sans t-text-[14px] t-font-semibold t-leading-[1.45] t-text-navy">{text}</p>
    </div>
  );
};

const PurchaseCardDetails = ({
  duration,
  focusArea = [],
  totalIngredients = 0,
  ingredients = [],
  perDayPriceLabel,
  testimonialsSlot = null,
  promotionSlot = null,
  proofTitleClassName,
  availabilityContent = null,
  showShippingTrust = true,
  showSavingsSummary = true,
  showScientificName = true,
  showOmega3Benefit = false,
  omega3BenefitKey,
  showCancelAnytime = true,
}) => {
  const { t, i18n } = useTranslation();
  const isGerman = isGermanLanguage(i18n.language);
  const [isBenefitsOpen, setIsBenefitsOpen] = useState(false);
  const [areIngredientsOpen, setAreIngredientsOpen] = useState(false);
  const [isCancelInfoOpen, setIsCancelInfoOpen] = useState(false);
  const benefitsRef = useRef(null);
  const ingredientsRef = useRef(null);

  useEffect(() => {
    if (!isBenefitsOpen || !benefitsRef.current || window.innerWidth < 1024) return undefined;
    const timeoutId = window.setTimeout(() => {
      benefitsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 100);
    return () => window.clearTimeout(timeoutId);
  }, [isBenefitsOpen]);

  useEffect(() => {
    if (!areIngredientsOpen || !ingredientsRef.current || window.innerWidth < 1024) return undefined;
    const timeoutId = window.setTimeout(() => {
      ingredientsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 100);
    return () => window.clearTimeout(timeoutId);
  }, [areIngredientsOpen]);

  const defaultAvailabilityContent = (
    <>
      {isGerman ? t('Product_Availability') : <>
        <span className="t-font-semibold t-text-green-500">{t(Available)}</span>
        {' — '}
        <span className="t-font-semibold t-text-navy">{t(OrderNow)}</span>
      </>} <span>{t(AvailableText)}</span>
    </>
  );

  return (
    <>
      <div className="t-flex t-flex-row t-items-start t-gap-2">
        <div className="t-mt-[5px] t-h-2 t-w-2 t-flex-none t-rounded-full t-bg-green-500" />
        <p className="!t-m-0 t-font-sans t-text-[13px] t-text-navy/70">
          {availabilityContent || defaultAvailabilityContent}
        </p>
      </div>

      {promotionSlot}

      <div className="!t-mt-8 t-flex t-items-start t-justify-center t-gap-8">
        <InfoCard image={PaymentMethods} desc={t(SecurePayment)} className="md:!t-w-1/2" />
        {showShippingTrust && (
          <InfoCard image={ShippingImg} desc={t(ShippedIn2To3Days)} pClass="md:t-mt-[10px]" />
        )}
        <InfoCard image={ReviewsImage} desc={t(Verified)} pClass="md:t-mt-[10px]" />
      </div>

      <ProofSection titleClassName={proofTitleClassName} />

      {testimonialsSlot}

      <div
        ref={benefitsRef}
        className="t-flex t-flex-col t-gap-2 t-rounded-xl t-border-2 t-border-g200 t-p-3"
      >
        <button
          type="button"
          className="t-flex t-w-full t-cursor-pointer t-items-center t-justify-between t-gap-2 t-border-0 t-bg-transparent t-p-0 t-text-left"
          onClick={() => setIsBenefitsOpen((isOpen) => !isOpen)}
        >
          <span className="t-font-sans t-text-[14px] t-font-medium t-text-navy">
            {isGerman
              ? t('Product_BenefitsHeading', { price: perDayPriceLabel })
              : <>{t(WhatAreYouGetting)}{' '}<span className="t-font-semibold t-text-quiz-teal">{perDayPriceLabel}</span> {t(PerDay)}:</>}
          </span>
          <Icon name={isBenefitsOpen ? 'minus' : 'plus'} />
        </button>
        {isBenefitsOpen && (
          <>
            <BenefitsList
              duration={duration}
              focusArea={focusArea}
              totalIngredients={totalIngredients}
              showOmega3Benefit={showOmega3Benefit}
              omega3BenefitKey={omega3BenefitKey}
            />
            {showSavingsSummary && (
              <p className="!t-m-0 t-rounded-xl t-bg-g100 t-p-2 t-text-center t-font-sans t-text-[13px] t-text-navy">
                {isGerman
                  ? t('Product_SavingsSummary')
                  : <>{t(YouAre)} <span className="t-font-semibold t-text-quiz-teal">{t(Saving)} 34%</span>{' '}{t(InAverage)}</>}
              </p>
            )}
          </>
        )}
      </div>

      {ingredients.length > 0 && (
        <div ref={ingredientsRef} className="t-flex t-flex-col t-gap-2 t-rounded-xl t-p-3">
          <button
            type="button"
            className="t-flex t-w-full t-cursor-pointer t-items-center t-justify-between t-gap-2 t-border-0 t-bg-transparent t-p-0 t-text-left"
            onClick={() => setAreIngredientsOpen((isOpen) => !isOpen)}
          >
            <span className="t-font-sans t-text-[14px] t-font-medium t-text-navy">{t(IngredientsLabel)}</span>
            <Icon name={areIngredientsOpen ? 'minus' : 'plus'} />
          </button>
          {areIngredientsOpen && (
            <IngredientLabelList ingredients={ingredients} showScientificName={showScientificName} />
          )}
        </div>
      )}

      {showCancelAnytime && (
        <div className="t-flex t-flex-col t-gap-2 t-rounded-xl t-p-3">
          <button
            type="button"
            className="t-flex t-w-full t-cursor-pointer t-items-center t-justify-between t-gap-2 t-border-0 t-bg-transparent t-p-0 t-text-left"
            onClick={() => setIsCancelInfoOpen((isOpen) => !isOpen)}
          >
            <span className="t-font-sans t-text-[14px] t-font-medium t-text-navy">{t(CancelAnytimeLabel)}</span>
            <Icon name={isCancelInfoOpen ? 'minus' : 'plus'} />
          </button>
          {isCancelInfoOpen && (
            <p className="!t-m-0 t-font-sans t-text-[14px] t-leading-[1.45] t-text-navy">
              {t(CancelAnytimeAnswer)}
            </p>
          )}
        </div>
      )}

      <div className="t-px-1">
        <HowToEnjoyAccordion />
      </div>
    </>
  );
};

export default PurchaseCardDetails;
